import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: 400px;
  width: auto;

  @media (max-width: 768px) {
    top: 0.5rem;
    right: 0.5rem;
    max-width: calc(100% - 1rem);
  }

  @media (max-width: 480px) {
    position: relative;
    top: auto;
    right: auto;
    max-width: 100%;
    margin-top: 1rem;
    align-items: stretch;
  }
`;

const ToggleButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.ink};
  color: ${({ theme }) => theme.colors.ink};
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.ink};
    color: ${({ theme }) => theme.colors.paper};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Panel = styled.div`
  margin-top: 0.5rem;
  border: 1px solid #D4CFC4;
  border-radius: 8px;
  padding: 1rem;
  background: #FFFEF9;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 400px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;

  @media (max-width: 480px) {
    max-width: 100%;
    max-height: none;
  }
`;

const MessagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 320px;
  overflow-y: auto;
  margin-bottom: 1rem;
`;

const MessageBubble = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.9rem;
  line-height: 1.5;
  background: ${({ $isUser, theme }) =>
    $isUser ? theme.colors.manilaDark : theme.colors.manila};
  align-self: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  color: ${({ theme }) => theme.colors.ink};
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const InputRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const QuestionInput = styled.textarea`
  flex: 1;
  min-height: 60px;
  max-height: 140px;
  border: 1px solid #D4CFC4;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  resize: vertical;
  background: ${({ theme }) => theme.colors.paper};
  color: ${({ theme }) => theme.colors.ink};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.ink};
  }
`;

const SendButton = styled.button`
  padding: 0.6rem 1rem;
  border: none;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.ink};
  color: ${({ theme }) => theme.colors.paper};
  font-weight: 600;
  cursor: pointer;
  min-width: 90px;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StatusText = styled.p`
  font-size: 0.8rem;
  color: #8A7E70;
  margin: 0.25rem 0 0;
`;

const ErrorBanner = styled.div`
  border: 1px solid #D4847C;
  border-radius: 6px;
  padding: 0.75rem;
  color: #8A3A2A;
  background: #FBE9E6;
  font-size: 0.85rem;
  margin-top: 0.5rem;
`;

const SourcesList = styled.div`
  margin-top: 0.75rem;
  font-size: 0.8rem;
  color: #6B5F52;
`;

const truncateForPayload = (value, limit) => {
  if (!value) return '';
  if (value.length <= limit) return value;
  return `${value.slice(0, limit - 3)}...`;
};

const ResultContextAssistant = ({ result, searchQuery, apiBaseUrl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [sources, setSources] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  const metadataPayload = useMemo(
    () => ({
      file_path: result.file_path,
      summary: truncateForPayload(result.summary, 800),
      people: truncateForPayload(result.people, 400),
      locations: truncateForPayload(result.locations, 400),
      dates: truncateForPayload(result.dates, 400),
      ocr_content: truncateForPayload(result.ocr_content, 1800),
    }),
    [
      result.file_path,
      result.summary,
      result.people,
      result.locations,
      result.dates,
      result.ocr_content,
    ]
  );

  const resetState = useCallback(() => {
    setIsOpen(false);
    setMessages([]);
    setInputValue('');
    setIsLoading(false);
    setError(null);
    setTokenUsage(null);
    setSources([]);
    setLastUpdated(null);
  }, []);

  useEffect(() => {
    resetState();
  }, [resetState, result.file_path]);

  const askQuestion = useCallback(
    async (questionOverride = null) => {
      const prompt = (questionOverride ?? inputValue).trim();
      if (!prompt) return;

      setIsLoading(true);
      setError(null);

      const historyPayload = messages.map(({ role, content }) => ({
        role,
        content,
      }));

      try {
        const response = await fetch(`${apiBaseUrl}/result_context`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: searchQuery,
            question: prompt,
            result_id: result.file_path,
            result_metadata: metadataPayload,
            history: historyPayload,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || response.statusText || 'Request failed');
        }

        const data = await response.json();
        setMessages((prev) => [
          ...prev,
          { role: 'user', content: prompt },
          { role: 'assistant', content: data.answer || 'No answer provided.' },
        ]);
        setTokenUsage(data.token_usage || null);
        setSources(data.sources || []);
        setLastUpdated(new Date());
        setInputValue('');
      } catch (err) {
        setError(err.message || 'Unable to contact GPT assistant.');
      } finally {
        setIsLoading(false);
      }
    },
    [
      apiBaseUrl,
      inputValue,
      metadataPayload,
      messages,
      result.file_path,
      searchQuery,
    ]
  );


  const handleSubmit = (evt) => {
    evt.preventDefault();
    askQuestion();
  };

  return (
    <Container>
      <ToggleButton onClick={() => setIsOpen((prev) => !prev)} disabled={isLoading && !isOpen}>
        {isOpen ? 'Hide AI Insights' : 'Ask AI'}
      </ToggleButton>

      {isOpen && (
        <Panel>
          <MessagesList>
            {messages.map((message, index) => (
              <MessageBubble key={`${message.role}-${index}`} $isUser={message.role === 'user'}>
                {message.content}
              </MessageBubble>
            ))}
            {isLoading && <StatusText>Consulting chat...</StatusText>}
          </MessagesList>

          <form onSubmit={handleSubmit}>
            <InputRow>
              <QuestionInput
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                placeholder="Ask a follow-up question..."
                disabled={isLoading}
              />
              <SendButton type="submit" disabled={isLoading || !inputValue.trim()}>
                Send
              </SendButton>
            </InputRow>
          </form>

          {error && <ErrorBanner>{error}</ErrorBanner>}

          <StatusText>
            {lastUpdated
              ? `Last updated ${lastUpdated.toLocaleTimeString()} · ${
                  tokenUsage
                    ? `Tokens: ${tokenUsage.total_tokens || 'n/a'}`
                    : 'Token usage unavailable'
                }`
              : 'Answers are generated securely via the backend GPT service.'}
          </StatusText>

          {sources.length > 0 && (
            <SourcesList>
              Source: {sources.map((src) => src).join(', ')}
            </SourcesList>
          )}
        </Panel>
      )}
    </Container>
  );
};

export default ResultContextAssistant;

